import React, { FC, ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert } from 'antd';
import styled from '@emotion/styled';
import { pluralize } from '@app/utils/stringUtils';
import PageWithHeader from '@app/components/PageWithHeader';
import useLoadable from '@app/hooks/loadable/useLoadable';
import { addUserToOrganization } from '@app/services/userManagement/organizationUsersService';
import PageHeader, { PageHeaderBottomAlignedSideContent } from '@app/components/header/PageHeader';
import LinkButton from '@app/components/LinkButton';
import useInfraStores from '@app/hooks/useInfraStores';
import useAppStores from '@app/hooks/useAppStores';
import { useDebounce } from '@app/hooks/useDebounce';
import Loadable from '@app/utils/Loadable';
import { MultiselectValue } from '@app/components/tableFilters/multiValue/FilterColumnMultiValuePopup';
import BackeeMode from '@backee/backeeMode';
import OrganizationsTable, {
  OrganizationsTableState,
} from '@backee/routes/organizations/allOrganizationsPage/OrganizationsTable';
import { Organization } from '@app/domain/userManagement/organizations';
import { OrganizationalUserServerResponse } from '@app/domain/userManagement/organizationUsers';
import browserHistory from '@app/utils/browserHistory';
import { showCustomModalAsync } from '@app/components/Modal';
import AddUsersToOrganizationModal from '@app/components/userManagement/addUsersToOrganization/AddUsersToOrganizationModal';
import { ManageOrganizationTabType } from '@backee/routes/organizations/ManageOrganizationPage';
import useModalContext from '@app/hooks/useModalContext';
import ModalAppContext from '@app/ModalAppContext';
import { getUnassembledOrganizationIds, transformToOrganization } from '@backee/services/organizationsServices';
import { searchOrganizations } from '@app/services/userManagement/organizationsSearchService';

function renderUnassembledOrganizations(unassembledOrganizationIds: string[]): ReactNode {
  if (!unassembledOrganizationIds.length) {
    return null;
  }

  return (
    <UnassembledOrganizationsAlert
      data-testid='organizations-unassembled-alert'
      type='warning'
      showIcon
      message={`${unassembledOrganizationIds.length} ${pluralize('organization', unassembledOrganizationIds.length)} couldn't be loaded and ${
        unassembledOrganizationIds.length === 1 ? 'is' : 'are'
      } missing from the list`}
      description={
        <ul>
          {unassembledOrganizationIds.map((organizationId) => (
            <li key={organizationId} data-testid='organizations-unassembled-id'>
              {organizationId}
            </li>
          ))}
        </ul>
      }
    />
  );
}

const INITIAL_TABLE_STATE: OrganizationsTableState = {
  page: 1,
  pageSize: 25,
  sort: 'Name',
  order: 'ASC',
  serviceIds: [],
};

const AllOrganizationsPage: FC = observer(() => {
  const modalContext = useModalContext();
  const { navigationStore, permissionsStore } = useInfraStores<BackeeMode>();
  const { organizationsStore } = useAppStores<BackeeMode>();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm.trim());
  const [tableState, setTableState] = useState<OrganizationsTableState>(INITIAL_TABLE_STATE);

  useEffect(() => {
    setTableState((currentTableState) => (currentTableState.page === 1 ? currentTableState : { ...currentTableState, page: 1 }));
  }, [debouncedSearchTerm]);

  const [organizationsPage] = useLoadable(
    async (abortSignal): Promise<PaginatedData<Organization>> => {
      const organizationsPageResponse = await searchOrganizations(
        {
          query: debouncedSearchTerm || undefined,
          serviceIds: tableState.serviceIds,
          sort: tableState.sort,
          order: tableState.order,
          offset: (tableState.page - 1) * tableState.pageSize,
          limit: tableState.pageSize,
        },
        abortSignal,
      );

      return {
        content: organizationsPageResponse.content.map(transformToOrganization),
        totalElements: organizationsPageResponse.totalElements,
      };
    },
    [debouncedSearchTerm, tableState],
  );

  const [unassembledOrganizationIds] = useLoadable(getUnassembledOrganizationIds, []);

  const serviceTypeOptions: Loadable<MultiselectValue[]> = organizationsStore.serviceTypes.map((serviceTypes) =>
    serviceTypes.map((serviceType) => ({
      value: serviceType.id,
      text: serviceType.displayName,
      keywords: [serviceType.id, serviceType.displayName],
    })),
  );

  const renderHeaderSideContent = (): ReactNode => {
    if (!permissionsStore.isCreateOrganizationAllowed) {
      return null;
    }

    return (
      <PageHeaderBottomAlignedSideContent>
        <LinkButton id='link-create-organization' appearance='text' to={navigationStore.generateNewOrganizationsLink()}>
          + CREATE ORGANIZATION
        </LinkButton>
      </PageHeaderBottomAlignedSideContent>
    );
  };

  const handleOrganizationClick = (organization: Organization): void => {
    browserHistory.push(navigationStore.generateManageOrganizationLink(organization.id));
  };

  const handleOrganizationManageUsersClick = (organization: Organization): void => {
    browserHistory.push(navigationStore.generateManageOrganizationLink(organization.id, ManageOrganizationTabType.users));
  };

  function openAddNewUserToOrgModal(organization: Organization): void {
    showCustomModalAsync(
      (onDone) => {
        return (
          <ModalAppContext {...modalContext}>
            <AddUsersToOrganizationModal
              organizationId={organization.id}
              organizationName={organization.name}
              addUserToOrganization={(tenantUserId): Promise<OrganizationalUserServerResponse> =>
                addUserToOrganization(organization.id, tenantUserId)
              }
              onDone={onDone}
            />
          </ModalAppContext>
        );
      },
      {
        maskClosable: false,
      },
    );
  }

  return (
    <PageWithHeader width='full' header={<PageHeader sideContent={renderHeaderSideContent()}>All Organizations</PageHeader>}>
      {renderUnassembledOrganizations(unassembledOrganizationIds.result ?? [])}
      <OrganizationsTable
        id='all-organizations-table'
        organizationsPage={organizationsPage}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        tableState={tableState}
        onTableStateChange={setTableState}
        serviceTypeOptions={serviceTypeOptions}
        canAddUsersToOrganization={permissionsStore.isEditAllowed}
        onItemClick={handleOrganizationClick}
        onItemManageUsers={handleOrganizationManageUsersClick}
        onItemAddUsers={openAddNewUserToOrgModal}
      />
    </PageWithHeader>
  );
});

export default AllOrganizationsPage;

const UnassembledOrganizationsAlert = styled(Alert)`
  margin-bottom: 16px;

  ul {
    margin: 0;
    padding-left: 20px;
    user-select: text;
  }
`;
